// API service for communicating with Flask backend
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
// const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api'

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL
    this.token = localStorage.getItem('access_token')
    this._onUnauthorized = null
  }

  setToken(token) {
    this.token = token
    if (token) {
      localStorage.setItem('access_token', token)
    } else {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_data')
    }
  }

  setOnUnauthorized(callback) {
    this._onUnauthorized = callback
  }

  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    }
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    
    return headers
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`
    const config = {
      headers: this.getAuthHeaders(),
      ...options,
    }

    try {
      const response = await fetch(url, config)
      
      // Check if we can parse the response as JSON
      let data
      try {
        data = await response.json()
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError)
        throw new Error(`Invalid response format from server`)
      }

      if (!response.ok) {
        // Handle 401 Unauthorized - session expired
        if (response.status === 401 && this._onUnauthorized) {
          this.setToken(null)
          this._onUnauthorized()
          throw new Error('Session expired. Please log in again.')
        }

        console.error('API Error:', {
          status: response.status,
          statusText: response.statusText,
          endpoint: endpoint
        })

        const rawError = data?.error ?? data?.message ?? `HTTP error! status: ${response.status}`
        const normalizedErrorText = typeof rawError === 'string'
          ? rawError
          : JSON.stringify(rawError)

        const isTransientGatewayIssue = /json could not be generated|bad gateway|gateway timeout|cloudflare|\b502\b|temporarily unavailable/i
          .test(normalizedErrorText)

        if (isTransientGatewayIssue) {
          throw new Error('Service is temporarily unavailable (upstream 502). Please retry in a few seconds.')
        }

        throw new Error(data?.message || normalizedErrorText || `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error('API request failed:', {
        endpoint,
        method: config.method || 'GET',
        error: error.message
      })
      
      // Provide more specific error messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please ensure the backend is running.')
        // throw new Error('Unable to connect to server. Please ensure the backend is running on http://localhost:5001')
      }
      
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your internet connection and ensure the backend server is running.')
      }
      
      throw error
    }
  }

  // Auth endpoints
  async login(email, password) {
    const data = await this.makeRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    
    if (data.access_token) {
      this.setToken(data.access_token)
    }
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token)
    }
    
    return data
  }

  async register(email, password) {
    const data = await this.makeRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    
    if (data.access_token) {
      this.setToken(data.access_token)
    }
    
    return data
  }

  async logout() {
    this.setToken(null)
  }

  async getMe() {
    const data = await this.makeRequest('/auth/me', {
      method: 'GET',
    })
    return data
  }

  // Connection test method for debugging
  async testConnection() {
    try {
      const response = await fetch(`${this.baseUrl}/health`)
      const data = await response.json()
      return { success: true, data }
    } catch (error) {
      console.error('Backend Connection Failed:', error)
      return { success: false, error: error.message }
    }
  }

  // Expense endpoints
  async getExpenses(filters = {}) {
    const params = new URLSearchParams()
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key])
      }
    })
    
    const queryString = params.toString()
    const endpoint = `/expenses/${queryString ? `?${queryString}` : ''}`
    
    const data = await this.makeRequest(endpoint, {
      method: 'GET',
    })
    
    return data.expenses || []
  }

  async createExpense(expense) {
    const data = await this.makeRequest('/expenses/', {
      method: 'POST',
      body: JSON.stringify(expense),
    })
    
    return data.expense
  }

  async updateExpense(id, updates) {
    const data = await this.makeRequest(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
    
    return data.expense
  }

  async deleteExpense(id) {
    await this.makeRequest(`/expenses/${id}`, {
      method: 'DELETE',
    })
    
    return true
  }

  // Budget endpoints
  async getBudgets() {
    const data = await this.makeRequest('/expenses/budgets', {
      method: 'GET',
    })
    
    return data
  }

  async createBudget(budget) {
    const data = await this.makeRequest('/expenses/budgets', {
      method: 'POST',
      body: JSON.stringify(budget),
    })
    
    return data.budget
  }

  async updateBudget(id, updates) {
    const data = await this.makeRequest(`/expenses/budgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
    
    return data.budget
  }

  async deleteBudget(id) {
    await this.makeRequest(`/expenses/budgets/${id}`, {
      method: 'DELETE',
    })
    
    return true
  }

  // Budget Dashboard endpoints
  async getBudgetDashboard() {
    const data = await this.makeRequest('/budgets/budget-dashboard', {
      method: 'GET',
    })
    
    return data
  }

  async shareBudget(sharedWithEmail) {
    const data = await this.makeRequest('/budgets/share-budget', {
      method: 'POST',
      body: JSON.stringify({ shared_with_email: sharedWithEmail }),
    })
    
    return data
  }

  async unshareBudget(sharedWithEmail) {
    const data = await this.makeRequest('/budgets/unshare-budget', {
      method: 'DELETE',
      body: JSON.stringify({ shared_with_email: sharedWithEmail }),
    })
    
    return data
  }

  async getSharedWithMe() {
    const data = await this.makeRequest('/budgets/shared-with-me', {
      method: 'GET',
    })
    
    return data.shared_users || []
  }

  async getMyShares() {
    const data = await this.makeRequest('/budgets/my-shares', {
      method: 'GET',
    })
    
    return data.shared_with_users || []
  }

  // Profile endpoints
  async getProfile() {
    const data = await this.makeRequest('/profile/', {
      method: 'GET',
    })
    
    return data.profile
  }

  async updateProfile(profileData) {
    const data = await this.makeRequest('/profile/edit', {
      method: 'POST',
      body: JSON.stringify(profileData),
    })
    
    return data.profile
  }

  async uploadProfileImage(imageUrl) {
    const data = await this.makeRequest('/profile/upload-image', {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    })
    
    return data.profile
  }

  async getProfileStats() {
    const data = await this.makeRequest('/profile/stats', {
      method: 'GET',
    })
    
    return data.stats
  }

  async getSharedBudgets() {
    const data = await this.makeRequest('/budgets/shared-budgets', {
      method: 'GET',
    })
    
    return data.shared_budgets || []
  }

  // Monthly Archive endpoints
  async triggerMonthlyReset() {
    const data = await this.makeRequest('/monthly/reset', {
      method: 'POST',
    })
    
    return data
  }

  async getMonthlyArchives(year = null, month = null) {
    const params = new URLSearchParams()
    if (year) params.append('year', year)
    if (month) params.append('month', month)
    
    const queryString = params.toString()
    const endpoint = `/monthly/archives${queryString ? `?${queryString}` : ''}`
    
    const data = await this.makeRequest(endpoint, {
      method: 'GET',
    })
    
    return data.archives || []
  }

  async getArchivedExpenses(year, month) {
    const data = await this.makeRequest(`/monthly/archives/${year}/${month}/expenses`, {
      method: 'GET',
    })
    
    return data.expenses || []
  }

  async getArchivedBudgets(year, month) {
    const data = await this.makeRequest(`/monthly/archives/${year}/${month}/budgets`, {
      method: 'GET',
    })
    
    return data.budgets || []
  }

  // Keep-alive endpoints
  async pingDatabase() {
    const data = await this.makeRequest('/keepalive/ping', {
      method: 'GET',
    })
    
    return data
  }

  async getDatabaseStatus() {
    const data = await this.makeRequest('/keepalive/status', {
      method: 'GET',
    })
    
    return data
  }

  // Accounts
  async getAccounts() {
    const data = await this.makeRequest('/accounts/', {
      method: 'GET',
    })
    return data
  }

  async createAccount(payload) {
    const data = await this.makeRequest('/accounts/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.account
  }

  async updateAccount(accountId, payload) {
    const data = await this.makeRequest(`/accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    return data.account
  }

  async deleteAccount(accountId) {
    await this.makeRequest(`/accounts/${accountId}`, {
      method: 'DELETE',
    })
    return true
  }

  async transferAccounts(payload) {
    const data = await this.makeRequest('/accounts/transfer', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data
  }

  async getAccountTransactions(accountId = null) {
    const params = new URLSearchParams()
    if (accountId) {
      params.append('account_id', accountId)
    }
    const query = params.toString()
    const endpoint = `/accounts/transactions${query ? `?${query}` : ''}`

    const data = await this.makeRequest(endpoint, {
      method: 'GET',
    })
    return data.transactions || []
  }

  // Incomes
  async getIncomes(filters = {}) {
    const params = new URLSearchParams()
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key])
      }
    })

    const query = params.toString()
    const endpoint = `/incomes/${query ? `?${query}` : ''}`
    const data = await this.makeRequest(endpoint, {
      method: 'GET',
    })
    return data
  }

  async createIncome(payload) {
    const data = await this.makeRequest('/incomes/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.income
  }

  async updateIncome(incomeId, payload) {
    const data = await this.makeRequest(`/incomes/${incomeId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    return data.income
  }

  async deleteIncome(incomeId) {
    await this.makeRequest(`/incomes/${incomeId}`, {
      method: 'DELETE',
    })
    return true
  }

  async getIncomeStats() {
    const data = await this.makeRequest('/incomes/stats', {
      method: 'GET',
    })
    return data
  }

  // Bills
  async getBills(options = {}) {
    const params = new URLSearchParams()
    Object.keys(options).forEach(key => {
      if (options[key] !== undefined && options[key] !== null && options[key] !== '') {
        params.append(key, options[key])
      }
    })

    const query = params.toString()
    const endpoint = `/bills/${query ? `?${query}` : ''}`
    const data = await this.makeRequest(endpoint, {
      method: 'GET',
    })
    return data
  }

  async createBill(payload) {
    const data = await this.makeRequest('/bills/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.bill
  }

  async updateBill(billId, payload) {
    const data = await this.makeRequest(`/bills/${billId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    return data.bill
  }

  async payBill(billId, payload = {}) {
    const data = await this.makeRequest(`/bills/${billId}/pay`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data
  }

  async deleteBill(billId) {
    await this.makeRequest(`/bills/${billId}`, {
      method: 'DELETE',
    })
    return true
  }

  // Goals
  async getGoals(options = {}) {
    const params = new URLSearchParams()
    Object.keys(options).forEach(key => {
      if (options[key] !== undefined && options[key] !== null && options[key] !== '') {
        params.append(key, options[key])
      }
    })

    const query = params.toString()
    const endpoint = `/goals/${query ? `?${query}` : ''}`
    const data = await this.makeRequest(endpoint, {
      method: 'GET',
    })
    return data
  }

  async createGoal(payload) {
    const data = await this.makeRequest('/goals/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.goal
  }

  async updateGoal(goalId, payload) {
    const data = await this.makeRequest(`/goals/${goalId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    return data.goal
  }

  async contributeGoal(goalId, payload) {
    const data = await this.makeRequest(`/goals/${goalId}/contribute`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data
  }

  async deleteGoal(goalId) {
    await this.makeRequest(`/goals/${goalId}`, {
      method: 'DELETE',
    })
    return true
  }

  async getGoalStats() {
    const data = await this.makeRequest('/goals/stats', {
      method: 'GET',
    })
    return data
  }

  // Debts
  async getDebts(options = {}) {
    const params = new URLSearchParams()
    Object.keys(options).forEach(key => {
      if (options[key] !== undefined && options[key] !== null && options[key] !== '') {
        params.append(key, options[key])
      }
    })

    const query = params.toString()
    const endpoint = `/debts/${query ? `?${query}` : ''}`
    const data = await this.makeRequest(endpoint, {
      method: 'GET',
    })
    return data
  }

  async createDebt(payload) {
    const data = await this.makeRequest('/debts/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.debt
  }

  async updateDebt(debtId, payload) {
    const data = await this.makeRequest(`/debts/${debtId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    return data.debt
  }

  async payDebt(debtId, payload) {
    const data = await this.makeRequest(`/debts/${debtId}/pay`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data
  }

  async deleteDebt(debtId) {
    await this.makeRequest(`/debts/${debtId}`, {
      method: 'DELETE',
    })
    return true
  }

  async getDebtStats() {
    const data = await this.makeRequest('/debts/stats', {
      method: 'GET',
    })
    return data
  }

  // Collaboration
  async getWorkspaces() {
    const data = await this.makeRequest('/collaboration/workspaces', {
      method: 'GET',
    })
    return data
  }

  async createWorkspace(payload) {
    const data = await this.makeRequest('/collaboration/workspaces', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.workspace
  }

  async updateWorkspace(workspaceId, payload) {
    const data = await this.makeRequest(`/collaboration/workspaces/${workspaceId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    return data.workspace
  }

  async getWorkspaceMembers(workspaceId) {
    const data = await this.makeRequest(`/collaboration/workspaces/${workspaceId}/members`, {
      method: 'GET',
    })
    return data
  }

  async addWorkspaceMember(workspaceId, payload) {
    const data = await this.makeRequest(`/collaboration/workspaces/${workspaceId}/members`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.member
  }

  async updateWorkspaceMember(workspaceId, memberUserId, payload) {
    const data = await this.makeRequest(`/collaboration/workspaces/${workspaceId}/members/${memberUserId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    return data.member
  }

  async removeWorkspaceMember(workspaceId, memberUserId) {
    await this.makeRequest(`/collaboration/workspaces/${workspaceId}/members/${memberUserId}`, {
      method: 'DELETE',
    })
    return true
  }

  async getWorkspaceSummary(workspaceId) {
    const data = await this.makeRequest(`/collaboration/workspaces/${workspaceId}/summary`, {
      method: 'GET',
    })
    return data
  }

  // Notifications
  async getNotifications(options = {}) {
    const params = new URLSearchParams()
    Object.keys(options).forEach(key => {
      if (options[key] !== undefined && options[key] !== null && options[key] !== '') {
        params.append(key, options[key])
      }
    })

    const query = params.toString()
    const endpoint = `/notifications/${query ? `?${query}` : ''}`
    const data = await this.makeRequest(endpoint, {
      method: 'GET',
    })
    return data
  }

  async markNotificationRead(notificationId) {
    const data = await this.makeRequest(`/notifications/${notificationId}/read`, {
      method: 'POST',
    })
    return data.notification
  }

  async markNotificationUnread(notificationId) {
    const data = await this.makeRequest(`/notifications/${notificationId}/unread`, {
      method: 'POST',
    })
    return data.notification
  }

  async dismissNotification(notificationId) {
    const data = await this.makeRequest(`/notifications/${notificationId}/dismiss`, {
      method: 'POST',
    })
    return data.notification
  }

  async markAllNotificationsRead() {
    const data = await this.makeRequest('/notifications/mark-all-read', {
      method: 'POST',
    })
    return data
  }

  async getNotificationStats() {
    const data = await this.makeRequest('/notifications/stats', {
      method: 'GET',
    })
    return data
  }

  // Insights
  async getInsightsOverview() {
    const data = await this.makeRequest('/insights/overview', {
      method: 'GET',
    })
    return data
  }

  async getInsightsCashflow(months = 6) {
    const data = await this.makeRequest(`/insights/cashflow?months=${months}`, {
      method: 'GET',
    })
    return data
  }

  async getInsightsHealthScore() {
    const data = await this.makeRequest('/insights/health-score', {
      method: 'GET',
    })
    return data
  }

  async getInsightsSpendingByCategory(months = 1) {
    const data = await this.makeRequest(`/insights/spending-by-category?months=${months}`, {
      method: 'GET',
    })
    return data
  }

  async getInsightsDashboard() {
    const data = await this.makeRequest('/insights/dashboard', {
      method: 'GET',
    })
    return data
  }

  // Automation
  async runAutomation(payload = {}) {
    const data = await this.makeRequest('/automation/run', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data
  }

  async getAutomationStatus() {
    const data = await this.makeRequest('/automation/status', {
      method: 'GET',
    })
    return data
  }
}

// Export singleton instance
export const apiService = new ApiService()

// For backward compatibility, create service objects
export const authService = {
  login: (email, password) => apiService.login(email, password),
  register: (email, password) => apiService.register(email, password),
  logout: () => apiService.logout(),
  getMe: () => apiService.getMe(),
}

export const expenseService = {
  getExpenses: (filters) => apiService.getExpenses(filters),
  createExpense: (expense) => apiService.createExpense(expense),
  updateExpense: (id, updates) => apiService.updateExpense(id, updates),
  deleteExpense: (id) => apiService.deleteExpense(id),
}

export const budgetService = {
  getBudgets: () => apiService.getBudgets(),
  createBudget: (budget) => apiService.createBudget(budget),
  updateBudget: (id, updates) => apiService.updateBudget(id, updates),
  deleteBudget: (id) => apiService.deleteBudget(id),
}

export const budgetDashboardService = {
  getBudgetDashboard: () => apiService.getBudgetDashboard(),
  shareBudget: (email) => apiService.shareBudget(email),
  unshareBudget: (email) => apiService.unshareBudget(email),
  getSharedWithMe: () => apiService.getSharedWithMe(),
  getMyShares: () => apiService.getMyShares(),
  getSharedBudgets: () => apiService.getSharedBudgets(),
}

export const profileService = {
  getProfile: () => apiService.getProfile(),
  updateProfile: (profileData) => apiService.updateProfile(profileData),
  uploadProfileImage: (imageUrl) => apiService.uploadProfileImage(imageUrl),
  getProfileStats: () => apiService.getProfileStats(),
}

export const monthlyService = {
  triggerReset: () => apiService.triggerMonthlyReset(),
  getArchives: (year, month) => apiService.getMonthlyArchives(year, month),
  getArchivedExpenses: (year, month) => apiService.getArchivedExpenses(year, month),
  getArchivedBudgets: (year, month) => apiService.getArchivedBudgets(year, month),
}

export const keepAliveService = {
  ping: () => apiService.pingDatabase(),
  getStatus: () => apiService.getDatabaseStatus(),
}

export const accountService = {
  getAccounts: () => apiService.getAccounts(),
  createAccount: (payload) => apiService.createAccount(payload),
  updateAccount: (accountId, payload) => apiService.updateAccount(accountId, payload),
  deleteAccount: (accountId) => apiService.deleteAccount(accountId),
  transferAccounts: (payload) => apiService.transferAccounts(payload),
  getTransactions: (accountId) => apiService.getAccountTransactions(accountId),
}

export const incomeService = {
  getIncomes: (filters) => apiService.getIncomes(filters),
  createIncome: (payload) => apiService.createIncome(payload),
  updateIncome: (incomeId, payload) => apiService.updateIncome(incomeId, payload),
  deleteIncome: (incomeId) => apiService.deleteIncome(incomeId),
  getStats: () => apiService.getIncomeStats(),
}

export const billService = {
  getBills: (options) => apiService.getBills(options),
  createBill: (payload) => apiService.createBill(payload),
  updateBill: (billId, payload) => apiService.updateBill(billId, payload),
  payBill: (billId, payload) => apiService.payBill(billId, payload),
  deleteBill: (billId) => apiService.deleteBill(billId),
}

export const goalService = {
  getGoals: (options) => apiService.getGoals(options),
  createGoal: (payload) => apiService.createGoal(payload),
  updateGoal: (goalId, payload) => apiService.updateGoal(goalId, payload),
  contributeGoal: (goalId, payload) => apiService.contributeGoal(goalId, payload),
  deleteGoal: (goalId) => apiService.deleteGoal(goalId),
  getStats: () => apiService.getGoalStats(),
}

export const debtService = {
  getDebts: (options) => apiService.getDebts(options),
  createDebt: (payload) => apiService.createDebt(payload),
  updateDebt: (debtId, payload) => apiService.updateDebt(debtId, payload),
  payDebt: (debtId, payload) => apiService.payDebt(debtId, payload),
  deleteDebt: (debtId) => apiService.deleteDebt(debtId),
  getStats: () => apiService.getDebtStats(),
}

export const collaborationService = {
  getWorkspaces: () => apiService.getWorkspaces(),
  createWorkspace: (payload) => apiService.createWorkspace(payload),
  updateWorkspace: (workspaceId, payload) => apiService.updateWorkspace(workspaceId, payload),
  getWorkspaceMembers: (workspaceId) => apiService.getWorkspaceMembers(workspaceId),
  addWorkspaceMember: (workspaceId, payload) => apiService.addWorkspaceMember(workspaceId, payload),
  updateWorkspaceMember: (workspaceId, memberUserId, payload) => apiService.updateWorkspaceMember(workspaceId, memberUserId, payload),
  removeWorkspaceMember: (workspaceId, memberUserId) => apiService.removeWorkspaceMember(workspaceId, memberUserId),
  getWorkspaceSummary: (workspaceId) => apiService.getWorkspaceSummary(workspaceId),
}

export const notificationService = {
  getNotifications: (options) => apiService.getNotifications(options),
  markRead: (notificationId) => apiService.markNotificationRead(notificationId),
  markUnread: (notificationId) => apiService.markNotificationUnread(notificationId),
  dismiss: (notificationId) => apiService.dismissNotification(notificationId),
  markAllRead: () => apiService.markAllNotificationsRead(),
  getStats: () => apiService.getNotificationStats(),
}

export const insightService = {
  getOverview: () => apiService.getInsightsOverview(),
  getCashflow: (months) => apiService.getInsightsCashflow(months),
  getHealthScore: () => apiService.getInsightsHealthScore(),
  getSpendingByCategory: (months) => apiService.getInsightsSpendingByCategory(months),
  getDashboard: () => apiService.getInsightsDashboard(),
}

export const automationService = {
  run: (payload) => apiService.runAutomation(payload),
  getStatus: () => apiService.getAutomationStatus(),
}

export default apiService
